import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { Typography } from 'Components/atoms';
import { useI18n } from 'Hooks/useI18n';
import { tryParse } from 'Core-utils/helpers/ruleBuilderHelpers';
import {
    JSON_EDITOR_CONFIG_LABEL,
    JSON_EDITOR_FORMAT_BTN,
    JSON_EDITOR_CLEAR_BTN,
    JSON_EDITOR_EDIT_LABEL,
    JSON_EDITOR_PREVIEW_LABEL,
    JSON_EDITOR_INVALID_PREVIEW,
    JSON_EDITOR_EMPTY_PREVIEW,
} from 'Core-utils/constants/constants';

export type JsonConfigEditorProps = {
    value: string;
    error: string | null;
    onChange: (text: string, error: string | null) => void;
};

const JsonConfigEditor = ({ value, error, onChange }: JsonConfigEditorProps) => {
    const { t } = useI18n();

    // null when the text is empty or invalid JSON
    const parsed: unknown = useMemo(() => {
        if (!value.trim()) {
            return null;
        }
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }, [value]);

    const handleFormat = () => {
        if (parsed !== null) {
            onChange(JSON.stringify(parsed, null, 2), null);
        }
    };

    const handleChange = (text: string) => {
        const { error: err } = tryParse(text);
        onChange(text, text.trim() ? err : null);
    };

    return (
        <div className="d-flex flex-column gap-2 json-config-editor">
            <div className="d-flex justify-content-between align-items-center">
                <span className="json-config-editor__header-label">
                    {t(JSON_EDITOR_CONFIG_LABEL, 'Config (JSON)')}
                </span>
                <div className="d-flex gap-2">
                    {parsed !== null && (
                        <button
                            type="button"
                            onClick={handleFormat}
                            className="json-config-editor__format-btn"
                            title={t(JSON_EDITOR_FORMAT_BTN, 'Auto-format JSON')}
                        >
                            {t(JSON_EDITOR_FORMAT_BTN, 'Format')}
                        </button>
                    )}
                    {value.trim() && (
                        <button
                            type="button"
                            onClick={() => onChange('', null)}
                            className="json-config-editor__clear-btn"
                            title={t(JSON_EDITOR_CLEAR_BTN, 'Clear config')}
                        >
                            {t(JSON_EDITOR_CLEAR_BTN, '✕ Clear')}
                        </button>
                    )}
                </div>
            </div>

            <div className="d-flex gap-3 json-config-editor__container">
                {/* Edit panel */}
                <div className="d-flex flex-column gap-1 flex-grow-1" style={{ flex: 1 }}>
                    <span className="json-config-editor__panel-label">
                        {t(JSON_EDITOR_EDIT_LABEL, 'Edit')}
                    </span>
                    <Form.Control
                        as="textarea"
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={'{\n  "key": "value"\n}'}
                        className={`json-config-editor__textarea${error ? ' json-config-editor__textarea--error' : ''}`}
                    />
                    {error && (
                        <Typography variant="caption1" className="mt-1 d-block json-config-editor__error">
                            ⚠ Invalid JSON: {error}
                        </Typography>
                    )}
                </div>

                {/* Preview panel — uses JSONPretty (no dangerouslySetInnerHTML) */}
                <div className="d-flex flex-column gap-1" style={{ flex: 1 }}>
                    <span className="json-config-editor__panel-label">
                        {t(JSON_EDITOR_PREVIEW_LABEL, 'Preview')}
                    </span>
                    <div className="json-config-editor__preview-box">
                        {parsed !== null ? (
                            <JSONPretty
                                data={parsed}
                                className="json-config-editor__preview-pre"
                            />
                        ) : (
                            <div className="json-config-editor__preview-empty">
                                {value.trim()
                                    ? t(JSON_EDITOR_INVALID_PREVIEW, 'Invalid JSON — fix the editor to see preview')
                                    : t(JSON_EDITOR_EMPTY_PREVIEW, 'Enter JSON in the editor to see a formatted preview')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JsonConfigEditor;
